// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import MediaViewer, {type MediaViewerItem} from '@app/features/ui/state/MediaViewer';

interface MediaViewerOpenOptions {
	channelId?: string;
	messageId?: string;
	message?: Message;
	sourceChannel?: Channel | null;
	allowAttachmentDelete?: boolean;
}

type MediaViewerIntent =
	| {kind: 'open'; items: ReadonlyArray<MediaViewerItem>; currentIndex: number; options?: MediaViewerOpenOptions}
	| {kind: 'close'}
	| {kind: 'navigate'; index: number};

function dispatchMediaViewerIntent(intent: MediaViewerIntent): void {
	switch (intent.kind) {
		case 'open':
			MediaViewer.open(
				intent.items,
				intent.currentIndex,
				intent.options?.channelId,
				intent.options?.messageId,
				intent.options?.message,
				intent.options?.sourceChannel,
				intent.options?.allowAttachmentDelete,
			);
			return;
		case 'close':
			MediaViewer.close();
			return;
		case 'navigate':
			MediaViewer.navigate(intent.index);
			return;
	}
}

export function openMediaViewer(
	items: ReadonlyArray<MediaViewerItem>,
	currentIndex: number,
	options?: MediaViewerOpenOptions,
): void {
	dispatchMediaViewerIntent({kind: 'open', items, currentIndex, options});
}

export function closeMediaViewer(): void {
	dispatchMediaViewerIntent({kind: 'close'});
}

export function navigateMediaViewer(index: number): void {
	dispatchMediaViewerIntent({kind: 'navigate', index});
}

// KONVO: one viewer for every picture and clip in the app. Anything that has
// a URL (a WhatsApp blob, a mail attachment, a file) opens here too, so
// zoom, gestures and controls only ever need adding in one place. A picture
// without known dimensions is measured so the viewer can fit it: the one being
// opened first (it is usually on screen already, so that is quick), the rest
// once the viewer is up, so a tap never waits on a chat full of pictures.
export interface MediaViewerFile {
	src: string;
	filename: string;
	kind: 'image' | 'video';
	contentType?: string;
	width?: number | null;
	height?: number | null;
	fileSize?: number;
}

interface Size {
	width: number;
	height: number;
}

function measureImage(src: string): Promise<Size> {
	return new Promise((resolve) => {
		const image = new Image();
		image.onload = () => resolve({width: image.naturalWidth, height: image.naturalHeight});
		image.onerror = () => resolve({width: 0, height: 0});
		image.src = src;
	});
}

const needsMeasuring = (file: MediaViewerFile) => file.kind === 'image' && !(file.width && file.height);

function toItem(file: MediaViewerFile, size?: Size): MediaViewerItem {
	return {
		src: file.src,
		originalSrc: file.src,
		naturalWidth: size?.width ?? file.width ?? 0,
		naturalHeight: size?.height ?? file.height ?? 0,
		type: file.kind,
		filename: file.filename,
		contentType: file.contentType,
		fileSize: file.fileSize,
	};
}

/** Measures the viewer's unmeasured pictures and fills them in, if it is still showing the same set. */
function measureRest(session: number): void {
	const pending = MediaViewer.items.filter(
		(item) => item.type === 'image' && !(item.naturalWidth && item.naturalHeight),
	);
	if (!pending.length) return;
	void Promise.all(pending.map((item) => measureImage(item.src))).then((sizes) => {
		if (!MediaViewer.isOpen || MediaViewer.session !== session) return;
		const measured = new Map(pending.map((item, at) => [item.src, sizes[at]]));
		MediaViewer.replaceItems(
			MediaViewer.items.map((item) => {
				const size = measured.get(item.src);
				return size && !(item.naturalWidth && item.naturalHeight)
					? {...item, naturalWidth: size.width, naturalHeight: size.height}
					: item;
			}),
			MediaViewer.currentIndex,
		);
	});
}

export async function openMediaViewerForFile(file: MediaViewerFile): Promise<void> {
	await openMediaViewerForFiles([file], 0);
}

/**
 * Several pictures or clips at once (an album, a composer's pictures); arrows and swipes step through
 * them. Returns the viewer's session, for `updateMediaViewerFiles`, or null when nothing opened.
 */
export async function openMediaViewerForFiles(
	files: ReadonlyArray<MediaViewerFile>,
	index: number,
): Promise<number | null> {
	if (!files.length) return null;
	const start = Math.min(Math.max(index, 0), files.length - 1);
	const first = files[start];
	const firstSize = needsMeasuring(first) ? await measureImage(first.src) : undefined;
	openMediaViewer(
		files.map((file, at) => toItem(file, at === start ? firstSize : undefined)),
		start,
	);
	const session = MediaViewer.session;
	measureRest(session);
	return session;
}

/**
 * Swaps in a fuller set (pictures that were still loading when the viewer opened) while it stays on
 * the picture being looked at. Does nothing once the viewer has closed or opened something else.
 */
export function updateMediaViewerFiles(session: number, files: ReadonlyArray<MediaViewerFile>): void {
	if (!MediaViewer.isOpen || MediaViewer.session !== session || !files.length) return;
	const shown = new Map(MediaViewer.items.map((item) => [item.src, item]));
	const items = files.map((file) => {
		const item = toItem(file);
		const seen = shown.get(file.src);
		// Keep a size measured since the viewer opened.
		return seen && !(item.naturalWidth && item.naturalHeight)
			? {...item, naturalWidth: seen.naturalWidth, naturalHeight: seen.naturalHeight}
			: item;
	});
	const current = MediaViewer.getCurrentItem()?.src;
	const at = items.findIndex((item) => item.src === current);
	if (at < 0) return;
	MediaViewer.replaceItems(items, at);
	measureRest(session);
}
